import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Clock, ExternalLink, ShieldCheck, BookOpen } from 'lucide-react';
import axios from 'axios';
import { AuthorByline, AuthorBioBox } from '../components/AuthorBio';
import { SocialShare } from '../components/SocialShare';
import { RelatedPosts, MostPopularPosts } from '../components/BlogFeatures';
import { CommentsSection } from '../components/CommentsSection';
import { ClusterRail } from '../components/ClusterRail';
import { SEO, SchemaOrg } from '../components/SEO';
import { extractFAQ } from '../lib/extractFAQ';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

const renderMarkdown = (md = '') =>
  md
    .replace(/## /g, '<h2>')
    .replace(/### /g, '<h3>')
    .replace(/#### /g, '<h4>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/- (.*)/g, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)+/g, '<ul>$&</ul>');

const slugify = (name = '') =>
  name.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '');

export const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postRes, allPostsRes] = await Promise.all([
          axios.get(`${API}/blog/${slug}`),
          axios.get(`${API}/blog`),
        ]);
        setPost(postRes.data);
        setAllPosts(allPostsRes.data);
        // Fetch author profile for richer Person schema (best-effort)
        const authorName = postRes.data?.author_name;
        if (authorName) {
          try {
            const aRes = await axios.get(`${API}/authors/${slugify(authorName)}`);
            setAuthor(aRes.data);
          } catch {
            // Author endpoint optional — Person schema falls back to title-only
          }
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  // FAQ extraction is pure — memoize on content
  const faqItems = useMemo(() => extractFAQ(post?.content || ''), [post?.content]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6]">
        <p className="overline overline-gold mb-4">404</p>
        <h1 className="heading-editorial text-4xl mb-8">Story Not Found</h1>
        <Link to="/blog" className="btn-editorial">View All Articles</Link>
      </div>
    );
  }

  const currentIndex = allPosts.findIndex((p) => p.slug === slug);
  const prevPost = currentIndex > 0 ? allPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;

  return (
    <div data-testid={`blog-post-${slug}`} className="bg-[#FAF9F6]">
      <SEO
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        image={post.image_url}
        url={`/blog/${post.slug}`}
        type="article"
        article={{
          publishedAt: post.published_at,
          author: post.author_name,
          section: post.category,
        }}
        keywords={post.category}
      />
      <SchemaOrg
        type="article"
        data={{
          title: post.title,
          description: post.excerpt,
          image: post.image_url,
          publishedAt: post.published_at,
          modifiedAt: post.updated_at || post.published_at,
          author: post.author_name,
          authorTitle: post.author_title,
          authorUrl: post.author_name ? `/authors/${slugify(post.author_name)}` : null,
          authorSameAs: author?.sameAs || null,
          reviewer: post.reviewer_name || null,
          reviewerTitle: post.reviewer_title || null,
          reviewerUrl: post.reviewer_name ? `/authors/${slugify(post.reviewer_name)}` : null,
          citations: post.citations || [],
          url: `/blog/${post.slug}`,
        }}
      />
      {author ? (
        <SchemaOrg
          type="person"
          data={{
            name: author.name,
            jobTitle: author.title,
            bio: author.bio,
            image: author.photo_url || author.image,
            url: `/authors/${author.slug}`,
            sameAs: author.sameAs,
            knowsAbout: author.expertise,
            alumniOf: author.credentials,
          }}
        />
      ) : null}
      {faqItems.length > 0 ? (
        <SchemaOrg type="faqpage" data={{ items: faqItems }} />
      ) : null}
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'Articles', url: '/blog' },
            { name: post.title, url: `/blog/${post.slug}` },
          ],
        }}
      />

      {/* Editorial Header */}
      <article>
        <header className="border-b border-[#E5E0D8]">
          <div className="container-editorial py-12 lg:py-20">
            <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-12">
              <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-[#111] transition-colors">Articles</Link>
              <span>/</span>
              <span className="text-[#111] truncate max-w-[40ch]">{post.category || 'Story'}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
              <div className="lg:col-span-9">
                <p className="overline overline-gold mb-6">{post.category || 'Editorial'}</p>
                <h1 className="font-heading font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#111] mb-8">
                  {post.title}
                </h1>
                {post.excerpt && (
                  <p className="text-xl lg:text-2xl text-[#555] leading-relaxed font-light max-w-3xl">
                    {post.excerpt}
                  </p>
                )}
              </div>

              <div className="lg:col-span-3 lg:border-l border-[#E5E0D8] lg:pl-8">
                <AuthorByline
                  authorName={post.author_name}
                  authorTitle={post.author_title}
                  publishedAt={post.published_at}
                />

                {/* E-E-A-T: Peer reviewer badge */}
                {post.reviewer_name && (
                  <div
                    className="mt-5 pt-5 border-t border-[#E5E0D8]"
                    data-testid="reviewed-by-badge"
                  >
                    <p className="overline overline-gold mb-2 flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Reviewed By
                    </p>
                    <Link
                      to={`/authors/${slugify(post.reviewer_name)}`}
                      className="block group"
                    >
                      <p className="font-heading text-base text-[#111] group-hover:text-[#C69C6D] transition-colors">
                        {post.reviewer_name}
                      </p>
                      {post.reviewer_title && (
                        <p className="text-xs text-[#888] mt-0.5 leading-snug">
                          {post.reviewer_title}
                        </p>
                      )}
                    </Link>
                  </div>
                )}

                <div className="mt-6 pt-6 border-t border-[#E5E0D8] flex items-center gap-4 text-xs uppercase tracking-[0.15em] text-[#888] flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3" strokeWidth={1.5} />
                    5 min
                  </span>
                  <span className="text-[#C69C6D]">·</span>
                  <span>{formatDate(post.published_at)}</span>
                </div>

                {/* E-E-A-T: Last updated freshness signal */}
                {post.updated_at && post.updated_at !== post.published_at && (
                  <p
                    className="mt-3 text-[10px] tracking-[0.2em] uppercase text-[#888]"
                    data-testid="last-updated"
                  >
                    Last updated {formatDate(post.updated_at)}
                  </p>
                )}

                {/* E-E-A-T: Fact-checked banner */}
                {post.fact_checked_on && (
                  <Link
                    to="/methodology"
                    data-testid="fact-checked-banner"
                    className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#C69C6D] hover:text-[#111] border-b border-[#C69C6D] pb-0.5 transition-colors"
                  >
                    <ShieldCheck className="w-3 h-3" strokeWidth={1.5} />
                    Fact-checked {formatDate(post.fact_checked_on)}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Image */}
        <figure className="container-editorial pt-12 lg:pt-16">
          <div className="aspect-[16/9] overflow-hidden img-zoom">
            <img
              src={post.image_url}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
          <figcaption className="mt-4 text-xs tracking-wider uppercase text-[#888]">
            Photography · Editorial
          </figcaption>
        </figure>

        {/* Body */}
        <section className="container-editorial py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Article body */}
            <div className="lg:col-span-8 lg:col-start-2">
              <div
                className="prose-editorial"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
              />

              {/* E-E-A-T: Citations block (rendered if article has sourced citations) */}
              {Array.isArray(post.citations) && post.citations.length > 0 && (
                <div className="mt-12 pt-12 border-t border-[#E5E0D8]" data-testid="citations-block">
                  <p className="overline overline-gold mb-6 inline-flex items-center gap-2">
                    <BookOpen className="w-3 h-3" strokeWidth={1.5} />
                    Sources & Citations
                  </p>
                  <ol className="space-y-3 text-sm text-[#555] leading-relaxed list-none pl-0">
                    {post.citations.map((c, i) => (
                      <li key={i} className="flex gap-3 pb-3 border-b border-[#E5E0D8]/60">
                        <span className="font-heading text-[#C69C6D] text-base flex-shrink-0">{c.n || i + 1}</span>
                        <span>{c.text}</span>
                      </li>
                    ))}
                  </ol>
                  <Link
                    to="/methodology"
                    className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#C69C6D] hover:text-[#111] transition-colors"
                  >
                    Read our editorial methodology →
                  </Link>
                </div>
              )}

              {/* Designer CTA */}
              {post.designer_slug && (
                <div className="mt-16 pt-16 border-t border-[#E5E0D8]">
                  <p className="overline overline-gold mb-4">Continue Reading</p>
                  <div className="bg-[#111] text-white p-10 lg:p-14">
                    <h3 className="font-heading text-3xl lg:text-4xl font-light leading-tight mb-4">
                      Explore Pre-Owned{' '}
                      <span className="italic">
                        {post.designer_slug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                    </h3>
                    <p className="text-white/60 leading-relaxed mb-8 max-w-md">
                      Authenticated pieces from our trusted partners — curated for collectors and first-time buyers alike.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <Link
                        to={`/designers/${post.designer_slug}`}
                        className="btn-editorial border-white text-white hover:bg-white hover:text-[#111]"
                      >
                        Designer Guide
                      </Link>
                      <a
                        href="https://www.fashionphile.com/jewelry"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-editorial border-[#C69C6D] text-[#C69C6D] hover:bg-[#C69C6D] hover:text-white inline-flex items-center gap-2"
                      >
                        Shop Pre-Owned
                        <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Author Bio */}
              <div className="mt-16 pt-16 border-t border-[#E5E0D8]">
                <p className="overline overline-gold mb-6">About The Author</p>
                <AuthorBioBox authorName={post.author_name} />
              </div>

              {/* Share */}
              <div className="mt-12 pt-8 border-t border-[#E5E0D8] flex items-center justify-between flex-wrap gap-4">
                <p className="overline">Share This Story</p>
                <SocialShare
                  title={post.title}
                  url={`/blog/${post.slug}`}
                  description={post.excerpt}
                />
              </div>

              {/* Cluster Rail — sibling articles in the same topic cluster */}
              <ClusterRail postSlug={post.slug} />

              {/* Comments */}
              <CommentsSection postSlug={post.slug} postType="article" />
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-3 lg:col-start-10">
              <div className="sticky top-32">
                <p className="overline overline-gold mb-6">Most Popular</p>
                <MostPopularPosts posts={allPosts} />

                <div className="mt-12 pt-12 border-t border-[#E5E0D8]">
                  <p className="overline overline-gold mb-6">Quick Reads</p>
                  <ul className="space-y-4">
                    <li>
                      <Link
                        to="/guides/authenticate-luxury-jewelry"
                        className="block text-[#111] hover:text-[#C69C6D] transition-colors group"
                      >
                        <span className="block font-heading text-lg leading-tight">
                          Authentication Guide
                        </span>
                        <span className="overline mt-1 block">Read Now →</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/compare"
                        className="block text-[#111] hover:text-[#C69C6D] transition-colors"
                      >
                        <span className="block font-heading text-lg leading-tight">
                          Compare Resale Platforms
                        </span>
                        <span className="overline mt-1 block">Read Now →</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/partners"
                        className="block text-[#111] hover:text-[#C69C6D] transition-colors"
                      >
                        <span className="block font-heading text-lg leading-tight">
                          Trusted Partners
                        </span>
                        <span className="overline mt-1 block">Read Now →</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* Related Stories */}
        <section className="border-t border-[#E5E0D8]">
          <div className="container-editorial py-16 lg:py-20">
            <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
              <div>
                <p className="overline overline-gold mb-3">Continue The Story</p>
                <h2 className="heading-editorial text-3xl lg:text-4xl">More From The Magazine</h2>
              </div>
              <Link to="/blog" className="hidden md:inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase hover:text-[#C69C6D] transition-colors">
                All Articles <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
              </Link>
            </div>
            <RelatedPosts posts={allPosts} currentSlug={slug} />
          </div>
        </section>

        {/* Prev/Next */}
        <section className="border-t border-[#E5E0D8] bg-[#111] text-white">
          <div className="container-editorial py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {prevPost ? (
                <Link
                  to={`/blog/${prevPost.slug}`}
                  className="group flex items-start gap-4 md:border-r border-white/10 md:pr-8"
                >
                  <ArrowLeft className="w-4 h-4 mt-1.5 text-[#C69C6D] group-hover:-translate-x-1 transition-transform" strokeWidth={1.5} />
                  <div>
                    <p className="overline text-[#C69C6D] mb-2">Previous Story</p>
                    <p className="font-heading text-lg lg:text-xl text-white group-hover:text-[#C69C6D] transition-colors line-clamp-2">
                      {prevPost.title}
                    </p>
                  </div>
                </Link>
              ) : (
                <div />
              )}

              {nextPost ? (
                <Link
                  to={`/blog/${nextPost.slug}`}
                  className="group flex items-start gap-4 md:justify-end md:text-right md:pl-8"
                >
                  <div className="md:order-2">
                    <p className="overline text-[#C69C6D] mb-2">Next Story</p>
                    <p className="font-heading text-lg lg:text-xl text-white group-hover:text-[#C69C6D] transition-colors line-clamp-2">
                      {nextPost.title}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 mt-1.5 text-[#C69C6D] group-hover:translate-x-1 transition-transform md:order-3" strokeWidth={1.5} />
                </Link>
              ) : (
                <div />
              )}
            </div>
          </div>
        </section>
      </article>
    </div>
  );
};

export default BlogPostPage;
